import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReduxToastr from 'react-redux-toastr';

import { loginAsync } from 'src/features/auth';
import { Loading } from 'src/features/loading/Loading';
import { getCookie, COOKIES } from 'src/utils/cookie';

import { store } from './store';
import { Private } from './Private';
import { Public } from './Public';
import { setupInterceptors } from '../features/auth/setupInterceptors';
import './app.scss';
import { setupMessages } from '../features/messages/setupMessages';
import { getDriverInfo } from '../features/profile/store';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { LAUNCH_DARKLY_FEATURE_FLAGS } from 'src/constants/featureFlags';
import {
  setIsEmbedded,
  setPowerUnitNumber,
} from 'src/features/layout/Navigation/store/navigationStore';
import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { setFirestoreDriver, setFirestoreRuleset } from 'src/features/profile/store/profileStore';
import { FirestoreDriver } from 'src/features/profile/types';
import {
  calculateELDStateForData,
  mapDataFromFirestoreDocs,
  queryDriverDocs,
} from 'src/helpers/firestoreDriverHelpers';

/* eslint-disable no-undef */
const App = (): JSX.Element => {
  const dispatch = useDispatch();
  const driverInfo = useSelector(getDriverInfo);
  const AUTH_TOKEN = getCookie(COOKIES.AUTH_SERVICE_TOKEN);

  setupInterceptors(store);

  useEffect(() => {
    if (driverInfo.employeeId) {
      setupMessages(driverInfo);

      const app = initializeApp(window.MESSAGES);
      const db = getFirestore(app);

      const setFireStoreDriver = async () => {
        const eldDocs = await queryDriverDocs(db, driverInfo, driverInfo.companyId);
        if (eldDocs.length > 0) {
          dispatch(setFirestoreDriver(eldDocs[0].data() as FirestoreDriver));
          const mergedData = mapDataFromFirestoreDocs(eldDocs[0].data() as FirestoreDriver);
          const rulesetData = await calculateELDStateForData(mergedData);
          if (rulesetData) {
            dispatch(setFirestoreRuleset(rulesetData));
          }
        }
      };

      setFireStoreDriver();
      setInterval(setFireStoreDriver, 60000);
    }
  }, [driverInfo, dispatch]);

  useEffect(() => {
    dispatch(
      loginAsync({
        token: AUTH_TOKEN,
      }),
    );
    const queryParams = new URLSearchParams(location.search);
    let isEmbedded = queryParams.get('embedded');

    // If embedded query param is not available in storage, attempt to get it from URL
    if (!isEmbedded) {
      isEmbedded = localStorage.getItem('isEmbedded');
    }

    dispatch(
      setIsEmbedded({
        isEmbedded: isEmbedded === 'true',
      }),
    );

    let powerUnitNumber = queryParams.get('powerUnitNumber');
    if (!powerUnitNumber) {
      powerUnitNumber = localStorage.getItem('powerUnitNumber');
    }
    if (powerUnitNumber) {
      dispatch(setPowerUnitNumber(powerUnitNumber));
    }
  }, [AUTH_TOKEN, dispatch]);

  return (
    <div className="app">
      <Loading />
      <ReduxToastr
        timeOut={4000}
        newestOnTop={false}
        preventDuplicates
        position="bottom-right"
        transitionIn="fadeIn"
        transitionOut="fadeOut"
        progressBar
        closeOnToastrClick
      />
      {AUTH_TOKEN ? <Private /> : <Public />}
    </div>
  );
};

const featureFlagProvider = withLDProvider({
  clientSideID: window.LD_CLIENT_SIDE_ID,
  // do not convert flag keys
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
  // only request and listen to these flags
  flags: {
    ...LAUNCH_DARKLY_FEATURE_FLAGS,
  },
});

export default featureFlagProvider(App);
